/**
 * Role/right permissions returned by the platform API at login and /profile.
 *
 * The API decides which rights are enforced (`enforced`) and maps stages to
 * rights (`stage_rights`), so rolling permissions out to another stage or
 * module needs no frontend change. Rights not listed in `enforced` guard no
 * API route and are treated as granted; with no payload loaded at all,
 * everything is denied. These flags only drive the UI; the API enforces them.
 */
export type RightFlag = 'view' | 'write' | 'delete' | 'submit' | 'review' | 'approve'

export interface PermissionsPayload {
  enforced: string[]
  stage_rights: Record<string, string>
  rights: Record<string, Partial<Record<RightFlag, boolean>>>
}

// Fired by the API client when the server answers PERMISSION_DENIED, so the
// AuthProvider re-fetches rights that may be stale (e.g. a session that
// started before the rights changed).
/** Hint shown on a disabled link into a module the user's role cannot open. */
export const noAccessReason = (moduleName: string): string => `You don't have access to ${moduleName}.`

export const PERMISSIONS_STALE_EVENT = 'app:permissions-stale'

// Kept out of the auth cookie: that cookie is shared across platform apps
// and size-limited.
const PERMISSIONS_STORAGE_KEY = 'app_permissions'

export const getStoredPermissions = (): PermissionsPayload | undefined => {
  try {
    const value = window.localStorage.getItem(PERMISSIONS_STORAGE_KEY)
    return value ? (JSON.parse(value) as PermissionsPayload) : undefined
  } catch {
    return undefined
  }
}

export const storePermissions = (permissions: PermissionsPayload | undefined) => {
  try {
    if (permissions) {
      window.localStorage.setItem(PERMISSIONS_STORAGE_KEY, JSON.stringify(permissions))
    } else {
      window.localStorage.removeItem(PERMISSIONS_STORAGE_KEY)
    }
  } catch {
    // Storage unavailable (private mode / blocked): permissions stay in memory only.
  }
}

export const hasRight = (
  permissions: PermissionsPayload | undefined,
  rightKey: string | undefined,
  flag: RightFlag
): boolean => {
  // No payload (not loaded yet, or the /profile refresh failed): deny.
  if (!permissions) {
    return false
  }

  // A right the API does not enforce guards no API route, so there is nothing to deny.
  if (!rightKey || !permissions.enforced?.includes(rightKey)) {
    return true
  }

  return permissions.rights?.[rightKey]?.[flag] === true
}

export const stageRightKey = (
  permissions: PermissionsPayload | undefined,
  dkStage: string | undefined
): string | undefined => (dkStage ? permissions?.stage_rights?.[dkStage] : undefined)

